import { green, grey, orange, red } from "@material-ui/core/colors";
import { capitalize } from "lodash";
import type { UseTranslateResult } from "@tolgee/react";
import { ExpandedOrder, OrderProductStatus, OrderStatus } from "ts/api/order";
import Auth from "ts/api/auth";
import OrderReview, { OrderReviewStatus } from "ts/api/orderReview";
import { PaymentMethodType } from "ts/api/paymentMethod";
import { IRefund } from "@evennt-tech/api-v2-core";
import { getOrderTotalSupplierAmount } from "tools/order/getTotalOrderAmount";
import { RefundCalculator } from "@evennt-tech/api-v2-payment";
import { getOrderRefundStatus } from "tools/order/refund";

export type OrderStatusMap = {
  label: string;
  translationKey?: string;
  color?: string;
  shouldStrike?: boolean;
};
type OrderStatusesMapObject = {
  [k in
    | Exclude<OrderStatus, "draft">
    | "cancelled"
    | "notProvided"
    | "preAuthorised"
    | "updated"
    | "cancelledAndRefunded"
    | "partiallyRefunded"
    | "refundRequested"
    | "refundProcessing"
    | "refunded"
    | "cancelledAndPartiallyRefunded"]: OrderStatusMap;
};
type OrderApproveStatusesMapObject = {
  [k in OrderReviewStatus]: OrderStatusMap;
};

export const orderStatusesMap: OrderStatusesMapObject = {
  paid: {
    label: "Paid",
    translationKey: "order.status.paid",
    color: green[500],
  },
  processing: {
    label: "Processing",
    translationKey: "order.status.processing",
    color: grey[500],
  },
  reviewCompleted: {
    label: "Awaiting Payment",
    translationKey: "order.status.awaiting_payment",
    color: orange[500],
  },
  awaitingPayment: {
    label: "Awaiting Payment",
    translationKey: "order.status.awaiting_payment",
    color: orange[500],
  },
  cancelled: {
    label: "Cancelled",
    translationKey: "order.status.cancelled",
    color: red[500],
  },
  notProvided: {
    label: "Not Provided",
    translationKey: "order.status.not_provided",
    color: grey[900],
  },
  reviewProcess: {
    label: "Waiting for Approval",
    translationKey: "order.status.waiting_for_approval",
    color: orange[500],
  },
  preAuthorised: {
    label: "Pre-Authorised",
    translationKey: "order.status.pre_authorised",
  },
  updated: {
    label: "Multiple",
    translationKey: "order.status.multiple",
    color: orange[500],
  },
  cancelledAndRefunded: {
    label: "Cancelled & Refunded",
    translationKey: "order.status.cancelled_and_refunded",
    color: red[500],
  },
  partiallyRefunded: {
    label: "Paid (Partially Refunded)",
    translationKey: "order.status.partially_refunded",
    color: green[500],
  },
  refundRequested: {
    label: "Refund Requested",
    translationKey: "order.status.refund_requested",
    color: orange[500],
  },
  refundProcessing: {
    label: "Refund Processing",
    translationKey: "order.status.refund_processing",
    color: orange[500],
  },
  refunded: {
    label: "Refunded",
    translationKey: "order.status.refunded",
    color: orange[500],
  },
  cancelledAndPartiallyRefunded: {
    label: "Cancelled (Partially Refunded)",
    translationKey: "order.status.cancelled_partially_refunded",
    color: red[500],
  },
};
export const orderProductStatusTranslationKeys: Record<
  OrderProductStatus,
  string
> = {
  reviewProcess: "order.status.waiting_for_approval",
  received: "order.status.received",
  processing: "order.status.processing",
  complete: "order.status.complete",
  inTransit: "order.status.in_transit",
  cancelled: "order.status.cancelled",
  delivered: "order.status.delivered",
};
/** A product or delivery status in the UI's language; English when it has no key. */
export const translateOrderProductStatus = (
  status: string,
  t: UseTranslateResult["t"]
): string => {
  const key = (orderProductStatusTranslationKeys as Record<
    string,
    string | undefined
  >)[status];

  return key
    ? t({ key, defaultValue: capitalize(status) })
    : capitalize(status);
};
export const orderReviewStatusesMap: OrderApproveStatusesMapObject = {
  waitingForApproval: {
    label: "Waiting for Approval",
    translationKey: "order.status.waiting_for_approval",
    color: orange[500],
  },
  rejected: {
    label: "Rejected",
    translationKey: "order.status.rejected",
    color: red[500],
  },
  approved: {
    label: "Approved",
    translationKey: "order.status.approved",
    color: green[500],
  },
};

// A card order that requires supplier approval is only authorised at checkout;
// Firestore still records it as `paid`, so the state is derived, never stored.
export const isPreAuthorisedOrder = (
  order: Pick<ExpandedOrder, "paymentType" | "review">
) =>
  order.paymentType === PaymentMethodType.cardPayment &&
  order.review?.status === OrderReviewStatus.waitingForApproval;

export const isBankTransferAwaitingApproval = (order: ExpandedOrder) =>
  order.paymentType === PaymentMethodType.bankTransfer &&
  order.paymentDetails.status === OrderStatus.reviewProcess;

export const isBankTransferAwaitingPayment = (order: ExpandedOrder) =>
  order.paymentType === PaymentMethodType.bankTransfer &&
  (order.paymentDetails.status === OrderStatus.awaitingPayment ||
    order.paymentDetails.status === OrderStatus.reviewCompleted ||
    order.paymentDetails.status === OrderStatus.reviewProcess ||
    // NOTE: Properly handles older bank transfer approval-required orders that were created in "processing" status
    order.review?.status === OrderReviewStatus.waitingForApproval);

const getSupplierStatus = (
  order: ExpandedOrder,
  refunds: IRefund[],
  supplierId: string,
  companyId: string
) => {
  const reviewStatus = order.review?.status;
  if (isBankTransferAwaitingApproval(order)) {
    return orderStatusesMap.awaitingPayment;
  }

  const showReviewStatusLabel =
    order.paymentDetails.status === OrderStatus.reviewProcess && reviewStatus;
  if (showReviewStatusLabel) {
    return orderReviewStatusesMap[reviewStatus];
  }

  const supplierCompanyId =
    companyId ||
    order.products.find(
      (product) => product.owner.contactPersonId === supplierId
    )?.owner?.id;

  const refundStatus = supplierCompanyId
    ? getOrderRefundStatus({ order, refunds, supplierCompanyId })
    : null;

  if (refundStatus?.label === "Cancelled & Refunded") {
    return refundStatus;
  }

  const isCancelledOrderForSupplier =
    order?.cancelReason && supplierCompanyId
      ? typeof order?.cancelReason[supplierCompanyId] === "string"
      : false;

  const isPartiallyCancelled =
    !!order.isPartiallyCancelled &&
    !isCancelledOrderForSupplier &&
    !!order.paymentDetails.paymentDate;

  if (isCancelledOrderForSupplier) {
    if (refundStatus?.label === orderStatusesMap.partiallyRefunded.label) {
      return orderStatusesMap.cancelledAndPartiallyRefunded;
    }
    return orderStatusesMap.cancelled;
  }
  if (order.paymentDetails.status === OrderStatus.cancelled) {
    return orderStatusesMap[OrderStatus.cancelled];
  }
  if (refundStatus) {
    return refundStatus;
  }

  if (isPreAuthorisedOrder(order)) {
    return orderStatusesMap.preAuthorised;
  }

  if (isPartiallyCancelled) {
    return orderStatusesMap.paid;
  }

  if (order.paymentDetails.status === OrderStatus.draft) {
    return { label: "" };
  }
  return orderStatusesMap[order.paymentDetails.status];
};

export const getOrderStatusMap = (
  order: ExpandedOrder,
  refunds: IRefund[],
  user?: Auth,
  companyId?: string
): OrderStatusMap => {
  if (user?.role === "supplier" && companyId) {
    // ?? Need to research wtf
    return getSupplierStatus(order, refunds, user.id, companyId);
  }
  if (order.review?.status === "rejected") {
    return orderReviewStatusesMap.rejected;
  }

  const refundedAmount = RefundCalculator.getTotalRefundsAmount(refunds, false);
  const totalSupplierOrderAmount = getOrderTotalSupplierAmount(order);

  if (totalSupplierOrderAmount && refundedAmount === totalSupplierOrderAmount) {
    return orderStatusesMap.cancelledAndRefunded;
  }

  const refundStatus = getOrderRefundStatus({ order, refunds });
  if (refundStatus?.label === "Cancelled & Refunded") {
    return refundStatus;
  }

  const isPartiallyCancelled =
    !!order.isPartiallyCancelled &&
    order.products.some((p) => p.status !== OrderProductStatus.cancelled);

  if (isPartiallyCancelled) {
    return orderStatusesMap.updated;
  }

  const isCancelledOrderForOtherUsers = !!Object.values(
    order?.cancelReason || {}
  ).length;
  if (isCancelledOrderForOtherUsers) {
    if (refundStatus?.label === orderStatusesMap.partiallyRefunded.label) {
      return orderStatusesMap.cancelledAndPartiallyRefunded;
    }
    return orderStatusesMap.cancelled;
  }
  if (refundStatus) {
    return refundStatus;
  }
  if (order.paymentDetails.status === OrderStatus.draft) {
    return { label: "" };
  }

  if (isPreAuthorisedOrder(order)) {
    return orderStatusesMap.preAuthorised;
  }

  if (isBankTransferAwaitingApproval(order)) {
    return orderStatusesMap.awaitingPayment;
  }

  return orderStatusesMap[order.paymentDetails.status];
};

export const getReviewOrderStatusMap = (
  orderReview: OrderReview
): OrderStatusMap => {
  return orderReviewStatusesMap[orderReview.status];
};

export default orderStatusesMap;
